import {
  AcceptedCountries,
  AcceptedCountriesEnum,
  Location,
} from "@/types/user.definition";
import { Building2, Globe, Input, Mail, MapPin, Phone, PhoneInput, formatPhoneNumberForDisplay } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LocationForm } from "../../../../components/molecules/LocationForm";
import { Facility } from "../../../../types/facility.definition";
import { isAcceptablePhoneNumber } from "../../../../utils/phone.utils";
import { isValidEmail } from "../../../../utils/security.utils";
import { isValidWebsite } from "../../../../utils/url.utils";
import { AboutUsTab } from "./AboutUsTab";
import { SocialMediaTab } from "./SocialMediaTab";

interface GeneralInfoTabProps {
  facility: Facility;
  setFacility: React.Dispatch<React.SetStateAction<Facility>>;
  onValidationChange?: (isValid: boolean) => void;
}

interface ValidationErrors {
  name?: string;
  phoneNumber?: string;
  contactEmail?: string;
  website?: string;
  location?:
  | {
    country?: AcceptedCountries;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  }
  | undefined;
}

interface LocationFormData {
  locations: Location[];
}

export const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({
  facility,
  setFacility,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  // The number the facility was loaded with: kept as is, it does not need the stricter rule.
  const storedPhone = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    storedPhone.current = facility.phoneNumber?.trim() || undefined;
    // Only when another facility is loaded, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility.id]);

  // Initialize react-hook-form for location management
  const { control, setValue, watch } = useForm<LocationFormData>({
    defaultValues: {
      locations: [
        {
          address: facility.location?.address || "",
          city: facility.location?.city || "",
          state: facility.location?.state || "",
          zip: facility.location?.zip || "",
          country: facility.location?.country || AcceptedCountriesEnum.SENEGAL,
          isPrimary: true,
          accessInformation: facility.location?.accessInformation || "",
        },
      ],
    },
  });

  // Watch locations and sync back to facility state
  const watchedLocations = watch("locations");
  useEffect(() => {
    if (watchedLocations && watchedLocations[0]) {
      const location = watchedLocations[0];
      setFacility((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          address: location.address || "",
          city: location.city || "",
          state: location.state || "",
          zip: location.zip || "",
          country: location.country || AcceptedCountriesEnum.SENEGAL,
          accessInformation: location.accessInformation || "",
        },
      }));
    }
  }, [watchedLocations, setFacility]);

  // Validation function
  const validateForm = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    // Validate required fields
    if (!facility.name?.trim()) {
      newErrors.name = t(
        "facilitySettings.generalInfo.errors.facilityNameRequired"
      );
    }

    if (!facility.phoneNumber?.trim()) {
      newErrors.phoneNumber = t(
        "facilitySettings.generalInfo.errors.phoneNumberRequired"
      );
    } else if (!isAcceptablePhoneNumber(facility.phoneNumber, storedPhone.current)) {
      // MED-1653: "+22112" passed the old 1-17 digit check and the API answered a generic error.
      newErrors.phoneNumber = t("common.validation.invalidPhoneNumber");
    }

    // MED-1653: the contact email is editable (the API saves it) and the website is checked,
    // instead of letting the API refuse them with "Erreur inattendue".
    if (facility.contactEmail?.trim() && !isValidEmail(facility.contactEmail.trim())) {
      newErrors.contactEmail = t("common.validation.invalidEmail");
    }
    if (!isValidWebsite(facility.website)) {
      newErrors.website = t("common.validation.invalidUrl");
    }

    // Validate location fields (optional but recommended)
    newErrors.location = {};

    if (facility.location) {
      if (!facility.location.address?.trim()) {
        newErrors.location.address = t(
          "facilitySettings.generalInfo.errors.streetAddressRecommended"
        );
      }

      if (!facility.location.city?.trim()) {
        newErrors.location.city = t(
          "facilitySettings.generalInfo.errors.cityRecommended"
        );
      }

      if (!facility.location.state?.trim()) {
        newErrors.location.state = t(
          "facilitySettings.generalInfo.errors.stateRecommended"
        );
      }
    }

    setErrors(newErrors);

    // Check if there are any errors (excluding location warnings)
    const hasRequiredErrors =
      newErrors.name || newErrors.phoneNumber || newErrors.contactEmail || newErrors.website;
    const hasLocationErrors =
      newErrors.location &&
      Object.values(newErrors.location).some((error) => error);

    const isValid = !hasRequiredErrors && !hasLocationErrors;

    onValidationChange?.(isValid);
    return isValid;
  }, [facility, onValidationChange, t]);

  // Validate on facility changes
  useEffect(() => {
    validateForm();
  }, [facility, validateForm]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Handle nested location properties
    if (name.startsWith("location.")) {
      const locationField = name.split(".")[1];
      setFacility((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value,
        },
      }));
    } else {
      setFacility((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Helper function to get input className with error state
  const getInputClassName = (fieldName: string) => {
    const baseClasses =
      "transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500";
    const error = errors[fieldName as keyof ValidationErrors];
    const isTouched = touched[fieldName];

    if (error && isTouched) {
      return `${baseClasses} border-red-300 focus:border-red-500 focus:ring-red-500`;
    }
    return baseClasses;
  };

  return (
    <div className="space-y-6">
      {/* Basic Information Section */}
      <div className="space-y-4">
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
            <Building2 className="w-4 h-4 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t("facilitySettings.generalInfo.basicInformation")}
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            name="name"
            value={facility.name}
            label={t("facilitySettings.generalInfo.facilityName")}
            onChange={handleInputChange}
            placeholder={t(
              "facilitySettings.generalInfo.facilityNamePlaceholder"
            )}
            error={errors.name}
            leftIcon={<Building2 size={16} />}
            className={getInputClassName("name")}
          />

          <div className="space-y-1">
            <PhoneInput
              name="phoneNumber"
              value={facility.phoneNumber}
              label={t("facilitySettings.generalInfo.phoneNumber")}
              onChange={handleInputChange}
              placeholder={t(
                "facilitySettings.generalInfo.phoneNumberPlaceholder"
              )}
              error={errors.phoneNumber}
              className={getInputClassName("phoneNumber")}
            />
          </div>
        </div>
      </div>

      {/* Address Section - Using LocationForm */}
      <LocationForm
        control={control}
        setValue={setValue}
        showResidency={false}
        showPrimary={false}
        singleLocationIndex={0}
      />

      {/* Contact Information Section */}
      <div className="space-y-4">
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-secondary-100">
            <Mail className="w-4 h-4 text-secondary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t("facilitySettings.generalInfo.contactInformation")}
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Input
              label={t("facilitySettings.generalInfo.emailAddress")}
              name="contactEmail"
              type="email"
              value={facility.contactEmail}
              onChange={handleInputChange}
              placeholder={t("facilitySettings.generalInfo.emailPlaceholder")}
              leftIcon={<Mail size={16} />}
              error={errors.contactEmail}
            />
          </div>

          <Input
            label={t("facilitySettings.generalInfo.website")}
            name="website"
            value={facility.website}
            onChange={handleInputChange}
            placeholder={t("facilitySettings.generalInfo.websitePlaceholder")}
            leftIcon={<Globe size={16} />}
            error={errors.website}
          />
        </div>
      </div>

      {/* About Us Section */}
      <AboutUsTab facility={facility} setFacility={setFacility} />

      {/* Social Media Section */}
      <SocialMediaTab facility={facility} setFacility={setFacility} />

      {/* Preview Card */}
      <div className="p-4 mt-6 bg-gradient-to-br rounded-xl border from-neutral-50 to-neutral-100 border-neutral-200">
        <h4 className="mb-3 text-sm font-semibold text-neutral-700">
          {t("facilitySettings.generalInfo.preview")}
        </h4>
        <div className="p-3 bg-white rounded-lg shadow-sm">
          <h5 className="mb-2 font-semibold text-neutral-900">
            {facility.name || t("facilitySettings.generalInfo.facilityName")}
          </h5>

          <div className="grid grid-cols-1 gap-2 text-xs text-neutral-500 sm:grid-cols-2">
            <div className="flex items-center space-x-2">
              <MapPin className="w-3 h-3" />
              <span>
                {facility.location.address ||
                  t("facilitySettings.generalInfo.previewLabels.address")}
                ,{" "}
                {facility.location.city ||
                  t("facilitySettings.generalInfo.previewLabels.city")}
                ,{" "}
                {facility.location.state ||
                  t("facilitySettings.generalInfo.previewLabels.state")}{" "}
                {facility.location.zip ||
                  t("facilitySettings.generalInfo.previewLabels.zip")}
                {facility.location.accessInformation && (
                  <> — {facility.location.accessInformation}</>
                )}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-3 h-3" />
              <span>
                {facility.phoneNumber
                  ? formatPhoneNumberForDisplay(facility.phoneNumber)
                  : t("facilitySettings.generalInfo.previewLabels.phone")}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="w-3 h-3" />
              <span>
                {facility.contactEmail ||
                  t("facilitySettings.generalInfo.previewLabels.email")}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-3 h-3" />
              <span>
                {facility.website ||
                  t("facilitySettings.generalInfo.previewLabels.website")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
